import * as React from 'react';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { usePickersContext } from '@mui/x-date-pickers/hooks'; // Hook for internal state

import 'dayjs/locale/en-gb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import { post } from 'aws-amplify/api';

import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { Alert, Backdrop, Button, CircularProgress, FormControl, 
  InputLabel, MenuItem, Select, SelectChangeEvent, Switch,
  FormControlLabel, Snackbar,
  DialogActions} from '@mui/material';
import TextField from '@mui/material/TextField';

import * as Utils from '../util/Utils';
import * as ATCAConstants from '../util/ATCAConstants'

import { Box } from '@mui/system';
import { ScheduleContext } from './ScheduleContext';

dayjs.extend(utc);

/*
start
start 1/1
start 5
start 5/2
start 2-4/2
*/

export default function ScheduleSimulationView() {
  const scheduleContext = React.useContext(ScheduleContext);
  const [openSnack, setOpenSnack] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');

  const [openBackdrop, setOpenBackdrop] = React.useState(false);
  
  const [simulationText, setSimulationText] = React.useState<any>({});
  const [arrayConfig, setArrayConfig] = React.useState<string>('');
  const [hideAntenna6, setHideAntenna6] = React.useState(false);

  const [plot, setPlot] = React.useState<any>(null);
  const [graphType, setGraphType] = React.useState<any>({});
  const [graphTypeList, setGraphTypeList] = React.useState<any[]>([]);

  const [plotSize, setPlotSize] = React.useState(0);

  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (ref) {
      const width = ref.current ? ref.current.offsetWidth : 0;
      const height = ref.current ? ref.current.offsetHeight : 0;

      setPlotSize(width < height ? width : height);
    }
  }, [ref.current]);
  
  React.useEffect(() => {
    const config = scheduleContext.getSimulationArrayConfig();
    if (config !== arrayConfig) {
      setArrayConfig(config);
      return;
    }
    setSimulationText('');
    setPlot(null);
    setGraphType({});
  }, [scheduleContext.schedule]);

  React.useEffect(() => {
    const scanName = graphType['scan_name'];
    const type = graphType['type'];
    const totalScans = getSimulationScans();
    const utcDate = dayjs.utc(scheduleContext.getSimulationDate()).format();
    if (type === 'UV') {
      setPlot(null);
      setOpenBackdrop(true);
      doSimulateUVPlot(totalScans, utcDate, scanName, !hideAntenna6);
    } else if (type === 'El') {
      if (plot) {
        setPlot(null);
        setOpenBackdrop(true);
        doSimulateElPlot(totalScans, utcDate);
      }
    }
  }, [graphType, arrayConfig, hideAntenna6]);

  function doSimulateText(scans: number[], utcDate: string): void {
    const getOperation = post({
      apiName: 'cabb',
      path: '/simulation_text',
      options: {
        headers: {'Accept': 'image/png'},
        body: {
          project: scheduleContext.projectName,
          schedule: scheduleContext.filename,
          startDateTime: utcDate,
          scans: scans
        }
      }
    });

    getOperation.response
    .then(response => response.body.json())
    .then((response: any) => {
      if (response['status'] === 'success') {
        const content = response['content']
        setSimulationText(response['content']);

        const summary = content['summary'];
        let scanNames = (summary||[]).map(
          (line: string) => line.split(' ')[0]
        );

        if ((scanNames||[]).length > 0) {
          scanNames.pop();
        }

        const graphs: any[] = [{
          display: 'Scans Elevation Graph',
          scan_name: '',
          type: 'El'
        }];
        if (arrayConfig) {
          for (const name of scanNames) {
            const scanName = name.trim().replace(/\x1B\[.*?m/g, '');
            graphs.push({
              display: scanName + ' UV Coverage',
              type: 'UV',
              scan_name: scanName
            });
          }
        }
    
        setGraphTypeList(graphs);
        setGraphType(graphs[0]);
      } else {
        const message = response['message'];
          setOpenSnack(true);
          setErrorMessage(message);
      }
    })
    .catch((err: any) => {
      console.error('Error fetching simulation text:', err);
      setErrorMessage('Error fetching simulation text: ' + err.toString())
    })
    .finally(() => {
      setOpenBackdrop(false);
    });
  }

  function doSimulateElPlot(scans: number[], utcDate: string): void {    
    const getOperation = post({
      apiName: 'cabb',
      path: '/simulation_el_plot',
      options: {
        headers: {'Accept': 'image/png'},
        body: {
          project: scheduleContext.projectName,
          schedule: scheduleContext.filename,
          startDateTime: utcDate,
          scans: scans
        }
      }
    });

    getOperation.response
    .then(response => response.body.blob()
    )
    .then((blob: any) => {
      setPlot({image: 
        URL.createObjectURL(blob)
      })
    })
    .catch((err: any) => {
      console.error('Error fetching simulation el plot:', err);
      setErrorMessage('Error fetching simulation el plot: ' + err.toString())
    })
    .finally(() => {
      setOpenBackdrop(false);
    });
  }

  function doSimulateUVPlot(scans: number[], utcDate: string, 
    scanName: string, includeAntenna6: boolean): void {

    const getOperation = post({
      apiName: 'cabb',
      path: '/simulation_uv_plot',
      options: {
        headers: {'Accept': 'image/png'},
        body: {
          project: scheduleContext.projectName,
          schedule: scheduleContext.filename,
          startDateTime: utcDate,
          scans: scans,
          array_config: arrayConfig,
          scan_name: scanName,
          include_antenna6: includeAntenna6
        }
      }
    });

    getOperation.response
    .then(response => response.body.blob()
    )
    .then((blob: any) => {
      setPlot({image: 
        URL.createObjectURL(blob)
      })
    })
    .catch((err: any) => {
      console.error('Error fetching uv el plot:', err);
      setErrorMessage('Error fetching uv el plot: ' + err.toString())
    })
    .finally(() => {
      setOpenBackdrop(false);
    });
  }

  const getSimulationScans = () => {
    let totalScans: number[] = [];
    const length = (scheduleContext.getScans()||[]).length;

    scheduleContext.getSimulationInstructions().forEach(line => {
      if (line.trim()) {
        const scans = Utils.instructionToScans(line, length);
        totalScans = totalScans.concat(scans);
      }
    });

    console.log('Total scans:', totalScans);
    return totalScans;
  }

  function runSimulation(): void {
    setOpenBackdrop(true);

    setSimulationText({});
    setPlot(null);

    const utcDate = dayjs.utc(scheduleContext.getSimulationDate()).format();
    const totalScans = getSimulationScans();

    doSimulateElPlot(totalScans, utcDate);
    doSimulateText(totalScans, utcDate);
  }

  function updateInstructions(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const str = event.target.value;
    const instructions = (str||'').split('\n');
    scheduleContext.updateSimulationInstructions(instructions);

    setPlot(null);
    setSimulationText('');
    setGraphTypeList([]);
  }

  function getColor(text: string): string {
    if (text.includes('[31m')) {
      return '#c62828';
    }

    if (text.includes('[35m')) {
      return '#4a148c';
    }

    return '';
  }

  function updateSimulationDate(e: any): void {
    scheduleContext.updateSimulationDate((e ? e : dayjs.utc()).format());
    setPlot(null);
    setSimulationText('');
    setGraphTypeList([]);
  }

  function updateArrayConfig(event: SelectChangeEvent<string>): void {
    scheduleContext.updateSimulationArrayConfig(event.target.value as string);
  }

  function CustomActionBar(props: any) {
    const { onAccept, className } = props;

    const handleToday = () => {
      let today = dayjs.utc().hour(0).minute(0).second(0).millisecond(0);
      scheduleContext.updateSimulationDate(today.format());
    };

    return (
      <DialogActions className={className}>
        {/* Your custom button */}
        <Button onClick={handleToday} size="small" color="secondary">
          Today
        </Button>
        
        {/* Default action bar buttons */}
        <Button onClick={onAccept} size="small">OK</Button>
      </DialogActions>
    );
  }

  const getIsBeforeToday = () => {
    const date = dayjs.utc(scheduleContext.getSimulationDate())
    const today = dayjs.utc().hour(0).minute(0).second(0).millisecond(0);

    if (date.isBefore(today)) {
      return true;
    }

    return false;
  }

  return (
    <React.Fragment>
      <Backdrop
        sx={
          (theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })
        }
        open={openBackdrop}
        onClick={e => setOpenBackdrop(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      
      <Snackbar
        role = "alert"
        aria-label = {"Alert message"}
        open={openSnack}
        autoHideDuration={6000}
        onClose={e => setOpenSnack(false)}
      >
        <Alert severity="error"> { errorMessage } </Alert>
      </Snackbar>

      <Stack direction={'row'} spacing={2} style={{ marginTop: 0 }}>
        <TextField
          id="outlined-multiline-flexible"
          label="Instructions"
          fullWidth
          multiline
          rows={6}
          sx={{ width: '60%', flexGrow: 1 }}
          value={scheduleContext.getSimulationInstructions().join('\n')}
          onChange={updateInstructions}
        />

        <Stack sx={{ minWidth: '250px', flexGrow: 1 }}  spacing={1}
          alignItems={'flex-end'} justifyContent={'space-between'}>

          <React.Fragment>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
              <DateTimePicker label="UTC start date and time" 
                sx={{ width: '100%' }} 
                format='YYYY-MM-DD HH:mm:ss'
                value={dayjs.utc(scheduleContext.getSimulationDate())}
                onChange={updateSimulationDate}
                closeOnSelect={false}
                slotProps={{
                  textField: {
                    error: getIsBeforeToday(),
                    helperText: getIsBeforeToday() ? 'Date is in the past' : '',
                  },
                }}
                slots={{
                  actionBar: CustomActionBar,
                }}
              />
            </LocalizationProvider>
          </React.Fragment>

          <Stack direction="row" spacing={0} alignItems='flex-end' width={'100%'}>
            <FormControl variant="outlined" sx={{ flexGrow: 4}}>
              <InputLabel id="array-label">Array Configuration</InputLabel>
              <Select
                labelId="array-label"
                displayEmpty={true}
                id="array"
                label="Array Configuration"
                value={arrayConfig}
                onChange={updateArrayConfig}
              >
                {
                  ATCAConstants.ARRAY_CONFIGURATION.map((config) => (
                    <MenuItem key={config['name']} value={config['name']}>{config['name']}</MenuItem>
                  ))
                }
              </Select>
            </FormControl>

            <Button variant="text" size='large'
              onClick={() => { window.open(ATCAConstants.ARRAY_HELP_URL, '_blank')}}
              startIcon={<OpenInNewOutlinedIcon fontSize='inherit' />}>
            </Button>

            <FormControlLabel sx={{ flexGrow: 1}}
              value={hideAntenna6}
              onChange={(e, checked) => {setHideAntenna6(checked);} }
              control={<Switch/>}
              label="Hide Antenna 6"
            />
          </Stack> 

          <Button color="primary" fullWidth
            aria-label="setting" variant="outlined"
            onClick={runSimulation}
          >
            Run Simulation
          </Button>
        </Stack>
      </Stack>

      <Stack direction={'row'} spacing={3}
        sx={{height: 'calc(100vh - 350px)'}}>
        <Box sx={{ padding: '5px',
          width: '55%', minWidth: '350px',
          border: '1px solid #eeeeee', overflowY: 'scroll'
        }}>
        {(simulationText['simulation']||[]).map((text: string, index: number) => (
          <Typography key={'simulation-text-' + index}
            margin={'5px'}
            color = {getColor(text)}
            variant="body2">
            {text.replace('\x1B[31m', '').replace('[m', '').replace('[35m', '')}
          </Typography>
        ))}
        {(simulationText['summary']||[]).map((text: string, index: number) => (
          <Typography key={'simulation-text-' + index} 
            margin={'1px 0px 1px 2px'}
            color={'#2e7d32'} variant="body1">
            {text.replace('\x1B[32m', '').replace('[m', '')}
          </Typography>
        ))}
        </Box>

        <Stack width={'100%'} direction={'column'} spacing={3} ref={ref}>
          { (graphTypeList.length > 0) &&
          <FormControl variant="outlined" sx={{ width: '50%'}}>
            <InputLabel id="array-label">Graph Type</InputLabel>
            <Select
              labelId="array-label"
              displayEmpty={true}
              id="plot"
              label="Graph Type"
              value={graphType}
              onChange={(e) => setGraphType(e.target.value)}
            >
              {
                graphTypeList.map((type) => (
                  <MenuItem key={type} value={type}>{type['display']}</MenuItem>
                ))
              }
            </Select>
          </FormControl>
          }

          {plot ?
            <figure style={{
              margin: 0,
              display: 'flex',
              width: plotSize * 1.2,
              flexDirection: 'column',
              justifyContent: 'center',
              flexGrow: 4}}>
              <img src={plot.image} alt={graphType}/> 
            </figure>
            : ''
          }
        </Stack>
      </Stack>
    </React.Fragment>
  );
}
